import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LeadForm = ({ variant = 'full', onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_address: '',
    purchase_price: '',
    loan_amount: '',
    loan_type: '',
    message: '',
  });

  const [errors, setErrors] = useState({});

  const loanTypes = [
    { value: 'fix_flip', label: t('loanTypes.fixFlip') },
    { value: 'bridge', label: t('loanTypes.bridge') },
    { value: 'rental', label: t('loanTypes.rental') },
    { value: 'commercial', label: t('loanTypes.commercial') },
    { value: 'refinance', label: t('loanTypes.refinance') },
    { value: 'foreclosure', label: t('loanTypes.foreclosure') },
    { value: 'eviction', label: t('loanTypes.eviction') },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    else if (formData.phone.replace(/\D/g, '').length < 10) newErrors.phone = 'Invalid phone';
    if (!formData.property_address.trim()) newErrors.property_address = 'Required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, loan_type: value }));
  };

  const formatPhoneNumber = (value) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length < 4) return phone;
    if (phone.length < 7) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price.replace(/,/g, '')) : null,
        loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount.replace(/,/g, '')) : null,
      };

      const response = await axios.post(`${API}/leads`, payload);
      
      if (response.data.success) {
        setSubmitted(true);
        // Google Ads conversion tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17679761657',
            'event_category': 'form',
            'event_label': 'lead_form_submission'
          });
        }
        toast.success(t('quote.thankYou'), {
          description: t('quote.received'),
        });
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Submission Failed', {
        description: 'Please try again or call us directly at 424-303-2130',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12" data-testid="form-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">{t('quote.thankYou')}</h3>
        <p className="font-body text-slate-600 mb-6">
          {t('quote.received')}
        </p>
        <a href="tel:424-303-2130" className="phone-link text-lg">
          {t('quote.callNow')} 424-303-2130
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <Label htmlFor="name" className="font-body font-medium text-slate-700">
            {t('quote.fullName')} *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            className={`input-institutional mt-2 rounded-none ${errors.name ? 'border-red-500' : ''}`}
            placeholder="John Smith"
            data-testid="input-name"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="font-body font-medium text-slate-700">
            {t('quote.email')} *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className={`input-institutional mt-2 rounded-none ${errors.email ? 'border-red-500' : ''}`}
            placeholder="john@example.com"
            data-testid="input-email"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone" className="font-body font-medium text-slate-700">
            {t('quote.phone')} *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handlePhoneChange}
            className={`input-institutional mt-2 rounded-none ${errors.phone ? 'border-red-500' : ''}`}
            placeholder="(555) 123-4567"
            data-testid="input-phone"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        {/* Loan Type */}
        <div>
          <Label className="font-body font-medium text-slate-700">
            {t('quote.loanType')}
          </Label>
          <Select onValueChange={handleSelectChange} value={formData.loan_type}>
            <SelectTrigger className="input-institutional mt-2 rounded-none" data-testid="select-loan-type">
              <SelectValue placeholder={t('quote.selectLoanType')} />
            </SelectTrigger>
            <SelectContent>
              {loanTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Property Address - Full Width */}
        <div className="md:col-span-2">
          <Label htmlFor="property_address" className="font-body font-medium text-slate-700">
            {t('quote.propertyAddress')} *
          </Label>
          <Input
            id="property_address"
            name="property_address"
            type="text"
            value={formData.property_address}
            onChange={handleChange}
            className={`input-institutional mt-2 rounded-none ${errors.property_address ? 'border-red-500' : ''}`}
            placeholder="123 Main St, City, State ZIP"
            data-testid="input-address"
          />
          {errors.property_address && <p className="text-red-500 text-sm mt-1">{errors.property_address}</p>}
        </div>

        {/* Purchase Price */}
        <div>
          <Label htmlFor="purchase_price" className="font-body font-medium text-slate-700">
            {t('quote.purchasePrice')}
          </Label>
          <div className="relative mt-2">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              id="purchase_price"
              name="purchase_price"
              type="text"
              value={formData.purchase_price}
              onChange={handleChange}
              className="input-institutional pl-8 rounded-none"
              placeholder="500,000"
              data-testid="input-purchase-price"
            />
          </div>
        </div>

        {/* Loan Amount */}
        <div>
          <Label htmlFor="loan_amount" className="font-body font-medium text-slate-700">
            {t('quote.loanAmount')}
          </Label>
          <div className="relative mt-2">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              id="loan_amount"
              name="loan_amount"
              type="text"
              value={formData.loan_amount}
              onChange={handleChange}
              className="input-institutional pl-8 rounded-none"
              placeholder="375,000"
              data-testid="input-loan-amount"
            />
          </div>
        </div>

        {/* Message - Full Width (only for full variant) */}
        {variant === 'full' && (
          <div className="md:col-span-2">
            <Label htmlFor="message" className="font-body font-medium text-slate-700">
              {t('quote.additionalDetails')}
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="input-institutional mt-2 rounded-none min-h-[120px]"
              placeholder={t('quote.additionalPlaceholder')}
              data-testid="input-message"
            />
          </div>
        )}
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading}
        className="btn-gold rounded-none w-full md:w-auto"
        data-testid="submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('quote.submitting')}
          </>
        ) : (
          t('quote.submit')
        )}
      </Button>

      <p className="font-body text-xs text-slate-500">
        {t('quote.consent')}
      </p>
    </form>
  );
};
